import * as THREE from "three";
import type { Rng } from "../rng";
import { TOWN } from "../config";
import { Tone } from "../materials";
import { terrainHeight } from "./terrain";
import { groundToneAt } from "./ground";
import { BUILDINGS, type Building } from "./buildings";
import { disc, rect, type Ground } from "./walkable";

export interface Town {
  group: THREE.Group;
  buildings: Building[];
  centre: [number, number];
  radius: number;
}

const GROUND_TONES = [Tone.white, Tone.paper, Tone.bone, Tone.linen] as const;

function plazaTone(x: number, z: number): number {
  const level = groundToneAt(x, z);
  return GROUND_TONES[Math.min(3, Math.max(0, Math.round(level * 3)))] as number;
}

function buildPlaza(centreX: number, centreZ: number, radius: number): THREE.Mesh {
  const size = radius * 2.4;
  const segments = Math.max(16, Math.round(size * 2.2));
  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
  geometry.rotateX(-Math.PI / 2);

  const position = geometry.getAttribute("position") as THREE.BufferAttribute;
  const colours = new Float32Array(position.count * 3);
  const colour = new THREE.Color();
  const plaza = new THREE.Color(Tone.linen);
  const worn = new THREE.Color(Tone.bone);

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i) + centreX;
    const z = position.getZ(i) + centreZ;
    position.setY(i, terrainHeight(x, z) + 0.03);

    const distance = Math.hypot(x - centreX, z - centreZ);
    const blend = 1 - smoothstep(radius * 0.62, radius * 1.05, distance);
    colour.set(plazaTone(x, z));
    const speckle = ((x * 7.13 + z * 3.71) % 1 + 1) % 1;
    colour.lerp(speckle > 0.72 ? worn : plaza, blend * 0.85);

    colours[i * 3] = colour.r;
    colours[i * 3 + 1] = colour.g;
    colours[i * 3 + 2] = colour.b;
  }

  position.needsUpdate = true;
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colours, 3));
  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }),
  );
  mesh.receiveShadow = true;
  mesh.position.set(centreX, 0, centreZ);
  return mesh;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export function buildTown(rng: Rng, ground: Ground): Town {
  const [centreX, centreZ] = TOWN.centre;
  const group = new THREE.Group();
  group.add(buildPlaza(centreX, centreZ, TOWN.plazaRadius));

  const buildings: Building[] = [];
  BUILDINGS.forEach((style, index) => {
    const [offsetX, offsetZ] = TOWN.layout[index] ?? [0, 0];
    const x = centreX + offsetX;
    const z = centreZ + offsetZ;
    const model = style.build(rng);
    const baseY = terrainHeight(x, z) - 0.04;
    model.position.set(x, baseY, z);
    model.rotation.y = TOWN.facing[index] ?? 0;
    group.add(model);

    const yaw = TOWN.facing[index] ?? 0;
    for (const surface of style.surfaces) {
      const world = { x: x + surface.x, z: z + surface.z, height: baseY + surface.height };
      ground.add(
        surface.kind === "disc"
          ? disc(world.x, world.z, surface.radius, world.height)
          : rect(world.x, world.z, surface.halfWidth, surface.halfDepth, world.height, yaw + surface.yaw),
      );
    }

    buildings.push({
      group: model,
      label: style.label,
      x,
      z,
      radius: TOWN.reach,
      surfaces: style.surfaces,
    });
  });

  return { group, buildings, centre: [centreX, centreZ], radius: TOWN.plazaRadius };
}
